import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <SEOHead 
          title="Password Reset Code Sent" 
          description="Check your email for password reset instructions"
          isIndexable={false}
        />
        <div className="max-w-md w-full space-y-8">
          {/* Success Header */}
          <div className="text-center">
            <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">📧</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Check Your Email
            </h2>
            <p className="text-purple-200">
              We've sent a password reset code to
            </p>
            <p className="text-white font-semibold text-lg mt-1">
              {email}
            </p>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-xl shadow-xl p-8 text-center">
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-center justify-center">
                <span className="text-green-500 mr-2 text-xl">✅</span>
                Password reset code sent successfully!
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              Please check your email for a 6-digit verification code. The code will expire in 30 minutes.
            </p>
            
            <button
              onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 transition duration-200"
            >
              Enter Reset Code
            </button>
            
            <div className="mt-6 text-sm text-gray-500">
              <p>Didn't receive the email? Check your spam folder.</p>
              <button
                onClick={() => {
                  setSent(false);
                  setEmail('');
                }}
                className="text-purple-600 hover:text-purple-800 mt-2"
              >
                Try a different email address
              </button>
            </div>
          </div>

          {/* Back to Login Link */}
          <div className="text-center">
            <Link
              to="/login"
              className="text-purple-200 hover:text-white transition duration-200"
            >
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Forgot Password" 
        description="Reset your password"
        isIndexable={false}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
            <span className="text-4xl">🔒</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Forgot Password?
          </h2>
          <p className="text-purple-200">
            No worries! Enter your email address and we'll send you a reset code.
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Enter your email address"
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Reset Code...
                </div>
              ) : (
                'Send Reset Code'
              )}
            </button>
          </form>

          {/* Additional Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Remember your password?{' '}
              <Link to="/login" className="text-purple-600 hover:text-purple-800 font-semibold">
                Sign In
              </Link>
            </p>
            <p className="mt-2">
              Don't have an account?{' '}
              <Link to="/register" className="text-purple-600 hover:text-purple-800 font-semibold">
                Sign Up
              </Link>
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-purple-800 bg-opacity-50 rounded-lg p-4 text-center">
          <p className="text-purple-100 text-sm">
            🔒 For security reasons, we don't reveal whether an email exists in our system.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;